// v0.60.4 (S2a + S5a) — device-local log of CONFIRMED sends. Feeds the
// "Recent sends" block on the Balance panel (the seed of the mini-statement).
// localStorage, same pattern as m.recent / m.nick: per browser (and per iOS
// Home-Screen partition), never sent to the Worker. Received payments arrive
// with chain-sourced Activity in v0.70 — until then this list is sends only,
// and is titled accordingly.

export interface TxLogEntry {
  txHash: string;
  to: string; // recipient address
  code?: string; // recipient wallet code at send time (if an M wallet)
  amount: string; // canonical USD string, e.g. "25" / "12.5"
  ref?: string; // display reference (deslugified), if any
  at: number; // epoch ms
}

const KEY = "m.txlog";
const MAX = 10; // THE recent — not a statement

export function readTxLog(): TxLogEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (r): r is TxLogEntry =>
          !!r &&
          typeof (r as TxLogEntry).txHash === "string" &&
          typeof (r as TxLogEntry).to === "string" &&
          typeof (r as TxLogEntry).amount === "string" &&
          typeof (r as TxLogEntry).at === "number",
      )
      .slice(0, MAX);
  } catch {
    return [];
  }
}

export function addTxLog(e: Omit<TxLogEntry, "at"> & { at?: number }): void {
  try {
    const entry: TxLogEntry = { ...e, at: e.at ?? Date.now() };
    const next = [
      entry,
      ...readTxLog().filter((x) => x.txHash !== entry.txHash),
    ].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage blocked — the list is a convenience, never required */
  }
}

/** "just now" · "5m" · "3h" · "2d" · else a short date. */
export function relTime(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
